import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  X, Send, Paperclip, Sparkles, User, UserRound, CheckCircle2, Loader2,
  FileText, ArrowRight, Pencil, ExternalLink, MessageCircle, Lightbulb, Camera,
  ArrowLeft, Image as ImageIcon,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Sheet, SheetContent, SheetClose, SheetTitle, SheetDescription } from "../../components/ui/sheet";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);
const PRIO_LABEL = { niedrig: "Niedrig", mittel: "Mittel", hoch: "Hoch" };
const CAT_LABEL = {
  webseite: "Website", inhalt: "Inhalt / Texte", bilder: "Bilder", technik: "Technik",
  domain: "Domain", email: "E-Mail", rechnung: "Rechnung", login: "Login / Zugang",
  cockpit: "Cockpit", modul: "Modul", allgemein: "Allgemeine Frage", sonstiges: "Sonstiges",
  support: "Support", anfrage: "Anfrage", aufgabe: "Aufgabe", freigabe: "Freigabe",
  statistik: "Statistik", dokument: "Dokument", paket: "Paket",
  neue_funktion: "Neue Funktion", shop: "Shop / Verkauf", seo: "SEO / Sichtbarkeit",
  kampagne: "Kampagne / Werbung", korrektur: "Korrektur",
};

export default function AiAssistDrawer({ open, onClose, clientId, accent }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const qs = clientId ? `?client_id=${clientId}` : "";
  const [automation, setAutomation] = useState(null);
  const generationEnabled = automation?.generation_enabled === true;
  const [view, setView] = useState("home"); // home | chat | wish

  // Radix kennt den tatsaechlichen Ausloeser nicht (er lebt in einer anderen, unabhaengigen
  // Seitenkomponente -- kein <Sheet.Trigger> moeglich). Deshalb selbst merken, was beim
  // Oeffnen fokussiert war, und beim Schliessen exakt dorthin zurueckgeben. Das Erfassen
  // muss waehrend des Renderns passieren (nicht in einem eigenen useEffect): Radix' interner
  // FocusScope-Mount-Effect eines Kind-Elements laeuft sonst zuerst und hat den Fokus schon
  // auf das erste fokussierbare Element im Dialog verschoben, bevor unser Effect drankaeme.
  const lastActiveRef = useRef(null);
  const prevOpenRef = useRef(false);
  if (open && !prevOpenRef.current) {
    lastActiveRef.current = document.activeElement;
  }
  prevOpenRef.current = open;

  // Chat-Zustand
  const [conv, setConv] = useState(null);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [chatAtts, setChatAtts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { ticket_no, human }
  const [starting, setStarting] = useState(false);

  // Wunsch-Zustand
  const [wishText, setWishText] = useState("");
  const [wishAtts, setWishAtts] = useState([]);
  const [wishCard, setWishCard] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [wishErr, setWishErr] = useState("");
  const [wishSubmitting, setWishSubmitting] = useState(false);
  const [wishDone, setWishDone] = useState(null); // { request_no }

  const [uploading, setUploading] = useState(false);
  const chatFileRef = useRef(null);
  const wishFileRef = useRef(null);
  const wishPhotoRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    let active = true;
    setAutomation(null);
    if (open) {
      api.get("/cockpit/ai/requests/config").then(({ data }) => {
        if (active) setAutomation(data.automation || null);
      }).catch(() => { if (active) setAutomation(null); });
    }
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setView("home");
      setConv(null); setInput(""); setChatAtts([]); setDone(null); setThinking(false);
      setWishText(""); setWishAtts([]); setWishCard(null); setWishErr(""); setWishDone(null);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conv, thinking]);

  const goSupportCompose = () => { onClose(); nav((clientId ? `/admin/cockpit/${clientId}/support` : "/cockpit/support") + "?compose=1"); };
  const goRequests = () => { onClose(); nav(clientId ? `/admin/cockpit/${clientId}/requests` : "/cockpit/requests"); };

  const startChat = () => {
    setView("chat");
    if (conv || starting) return;
    setStarting(true);
    api.post(`/cockpit/ai/start${qs}`).then((r) => setConv(r.data))
      .catch(() => toast.error(t("Der Assistent konnte nicht gestartet werden.")))
      .finally(() => setStarting(false));
  };

  const uploadFile = async (file, target) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/cockpit/ai/requests/upload${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const att = { url: data.url, label: data.label || file.name, content_type: data.content_type, is_image: data.is_image };
      if (target === "wish") setWishAtts((a) => [...a, att]);
      else setChatAtts((a) => [...a, att]);
      toast.success(t("Datei angehängt:") + " " + att.label);
    } catch (e) { toast.error(e.response?.data?.detail || t("Upload fehlgeschlagen")); }
    finally { setUploading(false); }
  };

  // ---- Chat ----
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || thinking || !conv) return;
    setInput("");
    setConv((c) => ({ ...c, messages: [...c.messages, { id: `tmp-${Date.now()}`, sender_type: "customer", message: text }] }));
    setThinking(true);
    try {
      const { data } = await api.post(`/cockpit/ai/${conv.id}/message${qs}`, { message: text });
      setConv(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("Nachricht konnte nicht gesendet werden.")); }
    finally { setThinking(false); }
  };
  const submitChat = async (human) => {
    if (!conv || submitting) return;
    setSubmitting(true);
    try {
      const url = `/cockpit/ai/${conv.id}/${human ? "human" : "send"}${qs}`;
      const { data } = await api.post(url, { attachments: chatAtts });
      setDone({ ticket_no: data.ticket_no, human });
    } catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht gesendet werden.")); }
    finally { setSubmitting(false); }
  };

  // ---- Wunsch ----
  const analyzeWish = async () => {
    if (analyzing || !generationEnabled) return;
    setAnalyzing(true); setWishErr("");
    try {
      const { data } = await api.post(`/cockpit/ai/requests/analyze${qs}`, { text: wishText.trim(), attachments: wishAtts });
      setWishCard(data.card);
      if (!data.ai_analyzed && data.ai_error) setWishErr(data.ai_error);
    } catch (e) { toast.error(e.response?.data?.detail || t("Analyse fehlgeschlagen")); }
    finally { setAnalyzing(false); }
  };
  const submitWish = async (withCard) => {
    if (wishSubmitting) return;
    if (!wishText.trim() && wishAtts.length === 0) { toast.error(t("Bitte beschreiben Sie Ihren Wunsch oder hängen Sie ein Bild an.")); return; }
    setWishSubmitting(true);
    try {
      const body = { text: wishText.trim(), attachments: wishAtts };
      if (withCard && wishCard) { body.card = wishCard; body.ai_analyzed = generationEnabled && !wishErr; }
      const { data } = await api.post(`/cockpit/ai/requests${qs}`, body);
      setWishDone({ request_no: data.request?.request_no });
    } catch (e) { toast.error(e.response?.data?.detail || t("Konnte nicht gesendet werden.")); }
    finally { setWishSubmitting(false); }
  };

  const draft = conv?.draft;
  // V146-Nachtrag: Waehrend irgendein echter Vorgang laeuft (Start, KI-Antwort,
  // Chat-Absenden, Wunsch-Analyse, Wunsch-Absenden, Datei-Upload), darf der Dialog
  // weder ueber X/Escape/Backdrop geschlossen noch per "Zurueck" verlassen werden --
  // sonst geht die laufende Anfrage verloren bzw. wirkt wie abgebrochen, obwohl sie
  // noch unterwegs ist. Nach Erfolg/Fehler ist busy wieder false (jeweils im finally).
  const busy = starting || thinking || submitting || analyzing || wishSubmitting || uploading;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { if (busy) return; onClose(); } }}>
      <SheetContent side="right" data-testid="ai-assist-drawer" showClose={false}
        onCloseAutoFocus={(e) => { e.preventDefault(); lastActiveRef.current?.focus?.(); }}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        className="w-full sm:max-w-[440px] bg-[#0C0D11] border-l border-white/10 flex flex-col shadow-2xl p-0 gap-0">
        <SheetTitle className="sr-only">{generationEnabled ? t("KEWAGPage KI-Assistent") : t("KEWAG Support")}</SheetTitle>
        <SheetDescription className="sr-only">{generationEnabled ? t("Kennt Ihr Konto & KEWAGPage") : t("Ohne KI direkt schreiben")}</SheetDescription>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 shrink-0">
          {view !== "home" && !done && !wishDone && (
            <button onClick={() => setView("home")} disabled={busy} data-testid="ai-back" className="p-1.5 -ml-2 text-gray-400 hover:text-white transition-colors disabled:opacity-30"><ArrowLeft className="w-5 h-5" /></button>
          )}
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1F`, color: accent }}><Sparkles className="w-5 h-5" /></span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm leading-tight">{generationEnabled ? t("KEWAGPage KI-Assistent") : t("KEWAG Support")}</div>
            <div className="text-[11px] text-gray-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} /> {generationEnabled ? t("Kennt Ihr Konto & KEWAGPage") : t("Ohne KI direkt schreiben")}</div>
          </div>
          <SheetClose asChild>
            <button disabled={busy} data-testid="ai-close" className="p-2 -mr-2 text-gray-400 hover:text-white transition-colors disabled:opacity-30">
              <X className="w-5 h-5" />
              <span className="sr-only">{t("Schliessen")}</span>
            </button>
          </SheetClose>
        </div>

        {!generationEnabled && (
          <p className="mx-4 mt-3 rounded-xl border border-amber-400/20 bg-amber-400/5 px-3 py-2 text-xs text-amber-200 shrink-0" role="status" data-testid="ai-availability">
            {automation?.message || t("Ohne KI prüft KEWAG Ihren Wunsch persönlich.")}
          </p>
        )}

        {/* ===== HOME: Quick Actions ===== */}
        {view === "home" && !wishDone && !done && (
          <div className="flex-1 overflow-y-auto px-5 py-6" data-testid="ai-home">
            <h3 className="text-lg font-black">{t("Wie können wir helfen?")}</h3>
            <p className="text-sm text-gray-400 mt-1">{t("Wählen Sie, was Sie tun möchten. Alles landet direkt bei KEWAG.")}</p>
            <div className="mt-5 space-y-3">
              <QuickAction accent={accent} icon={Lightbulb} testid="ai-qa-wish" title={t("Wunsch mitteilen")}
                desc={generationEnabled ? t("Text, Foto oder Screenshot senden — die KI fasst es für KEWAG zusammen.") : t("Ohne KI prüft KEWAG Ihren Wunsch persönlich.")} onClick={() => setView("wish")} primary />
              <QuickAction accent={accent} icon={MessageCircle} testid="ai-qa-ask" title={t("Frage stellen")}
                desc={t("Fragen Sie zu Ihrer Website, Ihrem Paket, Rechnungen und mehr.")} onClick={generationEnabled ? startChat : goSupportCompose} />
              <QuickAction accent={accent} icon={UserRound} testid="ai-qa-human" title={t("Mit Mensch sprechen")}
                desc={t("Ihr KEWAG-Team übernimmt Ihre Anfrage persönlich.")} onClick={goSupportCompose} />
            </div>
            <button onClick={goRequests} data-testid="ai-view-requests" className="mt-5 text-xs font-semibold text-gray-500 hover:text-gray-300 inline-flex items-center gap-1.5 transition-colors">
              <FileText className="w-3.5 h-3.5" /> {t("Meine bisherigen Wünsche ansehen")}
            </button>
          </div>
        )}

        {/* ===== WISH DONE ===== */}
        {wishDone && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="ai-wish-confirm">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${accent}1A`, color: accent }}><CheckCircle2 className="w-8 h-8" /></div>
            <div className="text-lg font-black">{t("Ihr Wunsch ist bei KEWAG eingegangen.")}</div>
            <p className="text-sm text-gray-400 mt-2">
              {t("Sie finden ihn jederzeit unter «Meine Wünsche» in Ihrem Cockpit.")}
              {wishDone.request_no && <><br /><span className="text-gray-300 font-semibold">{t("Wunsch-Nr.")}: {wishDone.request_no}</span></>}
            </p>
            <div className="flex flex-col gap-2 w-full mt-6 max-w-xs">
              <button onClick={goRequests} data-testid="ai-wish-goto" className="text-black font-bold rounded-xl py-3 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform" style={{ background: accent }}>{t("Zu meinen Wünschen")} <ArrowRight className="w-4 h-4" /></button>
              <button onClick={onClose} className="text-sm font-semibold text-gray-400 hover:text-white py-2 transition-colors">{t("Schliessen")}</button>
            </div>
          </div>
        )}

        {/* ===== WISH FLOW ===== */}
        {view === "wish" && !wishDone && (
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ai-wish">
            <div>
              <h3 className="text-base font-black">{t("Was wünschen Sie sich?")}</h3>
              <p className="text-xs text-gray-500 mt-1">{t("Beschreiben Sie Ihren Wunsch. Ein Bild oder Screenshot hilft KEWAG enorm.")}</p>
            </div>
            <textarea value={wishText} onChange={(e) => { setWishText(e.target.value); setWishCard(null); }} rows={4}
              placeholder={t("Zum Beispiel: «Bitte auf der Startseite oben ein neues Foto von unserem Team einsetzen.»")}
              data-testid="ai-wish-text"
              className="w-full resize-none bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-[color:var(--client-accent)]/50" />

            {/* Anhänge */}
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => wishPhotoRef.current?.click()} disabled={uploading} data-testid="ai-wish-photo"
                className="text-sm font-semibold rounded-xl py-2.5 px-3.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-40 transition-colors"><Camera className="w-4 h-4" /> {t("Foto / Screenshot")}</button>
              <button onClick={() => wishFileRef.current?.click()} disabled={uploading} data-testid="ai-wish-file"
                className="text-sm font-semibold rounded-xl py-2.5 px-3.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-40 transition-colors"><Paperclip className="w-4 h-4" /> {uploading ? t("Lädt…") : t("Datei")}</button>
            </div>
            {wishAtts.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="ai-wish-attachments">
                {wishAtts.map((a, i) => (
                  <div key={i} className="relative">
                    {a.is_image ? (
                      <img src={fileUrl(a.url)} alt={a.label} className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-gray-300 h-16"><Paperclip className="w-3 h-3" /> <span className="max-w-[110px] truncate">{a.label}</span></span>
                    )}
                    <button onClick={() => { setWishAtts((x) => x.filter((_, j) => j !== i)); setWishCard(null); }} data-testid={`ai-wish-remove-${i}`}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/20 text-gray-300 hover:text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Wunschkarte-Vorschau */}
            {wishCard && (
              <div className="rounded-2xl border p-4" style={{ borderColor: `${accent}55`, background: `${accent}0D` }} data-testid="ai-wish-card">
                <div className="flex items-center gap-2 mb-3"><FileText className="w-4 h-4" style={{ color: accent }} /><span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{t("Ihre Wunschkarte")}</span></div>
                <Row label={t("Betreff")} value={wishCard.subject} />
                <Row label={t("Kategorie")} value={wishCard.category_label || t(CAT_LABEL[wishCard.category] || wishCard.category)} />
                <Row label={t("Priorität")} value={wishCard.priority_label || t(PRIO_LABEL[wishCard.priority] || wishCard.priority)} />
                {wishCard.summary && <div className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{wishCard.summary}</div>}
                {wishCard.image_findings && <div className="text-xs text-gray-400 mt-2 flex items-start gap-1.5"><ImageIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} /> {wishCard.image_findings}</div>}
                {wishCard.next_step && <div className="text-xs text-gray-500 mt-2"><span className="font-semibold text-gray-400">{t("Nächster Schritt")}:</span> {wishCard.next_step}</div>}
              </div>
            )}
            {wishErr && <p className="text-xs text-amber-300" data-testid="ai-wish-error">{wishErr}</p>}
          </div>
        )}

        {/* WISH-Aktionsleiste */}
        {view === "wish" && !wishDone && (
          <div className="px-4 pt-3 pb-4 border-t border-white/10 space-y-2 shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
            {!wishCard ? (
              <>
                <button onClick={analyzeWish} disabled={!generationEnabled || analyzing || (!wishText.trim() && wishAtts.length === 0)} data-testid="ai-wish-analyze"
                  className="w-full text-black font-bold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform" style={{ background: accent }}>
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {analyzing ? t("KI wertet aus…") : t("Als Wunschkarte zusammenfassen")}
                </button>
                <button onClick={() => submitWish(false)} disabled={wishSubmitting || (!wishText.trim() && wishAtts.length === 0)} data-testid="ai-wish-send-direct"
                  className="w-full text-sm font-semibold rounded-xl py-2.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors">
                  {wishSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("Ohne KI direkt senden")}
                </button>
                <p className="text-[11px] text-gray-500 text-center">{t("Ohne KI prüft KEWAG Ihren Wunsch persönlich.")}</p>
              </>
            ) : (
              <>
                <button onClick={() => submitWish(true)} disabled={wishSubmitting} data-testid="ai-wish-send"
                  className="w-full text-black font-bold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform" style={{ background: accent }}>
                  {wishSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("An KEWAG senden")}
                </button>
                <button onClick={() => setWishCard(null)} data-testid="ai-wish-edit"
                  className="w-full text-sm font-semibold rounded-xl py-2.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 flex items-center justify-center gap-1.5 transition-colors"><Pencil className="w-3.5 h-3.5" /> {t("Wunsch bearbeiten")}</button>
              </>
            )}
          </div>
        )}

        {/* ===== CHAT FLOW ===== */}
        {view === "chat" && (done ? (
          <ConfirmScreen done={done} accent={accent} t={t} onClose={onClose} goSupport={() => { onClose(); nav(clientId ? `/admin/cockpit/${clientId}/support` : "/cockpit/support"); }} />
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {!conv && (
                <div className="flex items-center justify-center h-full text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
              )}
              {conv?.messages?.map((m) => (<Bubble key={m.id} m={m} accent={accent} />))}
              {thinking && (
                <div className="flex items-center gap-2 text-xs text-gray-500 pl-11" data-testid="ai-thinking">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: accent }} /> {t("KI-Assistent schreibt…")}
                </div>
              )}
              {draft && (
                <div className="rounded-2xl border p-4 mt-2" style={{ borderColor: `${accent}55`, background: `${accent}0D` }} data-testid="ai-draft">
                  <div className="flex items-center gap-2 mb-3"><FileText className="w-4 h-4" style={{ color: accent }} /><span className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{t("Zusammenfassung für KEWAG")}</span></div>
                  <Row label={t("Betreff")} value={draft.subject} />
                  <Row label={t("Kategorie")} value={t(CAT_LABEL[draft.category] || draft.category)} />
                  <Row label={t("Priorität")} value={t(PRIO_LABEL[draft.priority] || draft.priority)} />
                  <div className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{draft.summary}</div>
                  {draft.next_step && <div className="text-xs text-gray-500 mt-2"><span className="font-semibold text-gray-400">{t("Nächster Schritt")}:</span> {draft.next_step}</div>}
                  {draft.attachments_hint && chatAtts.length === 0 && <div className="text-xs mt-2 flex items-start gap-1.5" style={{ color: accent }}><Paperclip className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {draft.attachments_hint}</div>}
                </div>
              )}
              {chatAtts.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="ai-attachments">
                  {chatAtts.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-gray-300">
                      <Paperclip className="w-3 h-3" /> <span className="max-w-[140px] truncate">{a.label}</span>
                      <button onClick={() => setChatAtts((x) => x.filter((_, j) => j !== i))} className="text-gray-500 hover:text-white"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {draft && (
              <div className="px-4 pt-3 border-t border-white/10 space-y-2 shrink-0">
                <button onClick={() => submitChat(false)} disabled={submitting} data-testid="ai-send-kewag"
                  className="w-full text-black font-bold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform" style={{ background: accent }}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("An KEWAG senden")}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { document.getElementById("ai-input")?.focus(); }} data-testid="ai-edit"
                    className="text-sm font-semibold rounded-xl py-2.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 flex items-center justify-center gap-1.5 transition-colors"><Pencil className="w-3.5 h-3.5" /> {t("Antwort bearbeiten")}</button>
                  <button onClick={() => chatFileRef.current?.click()} disabled={uploading} data-testid="ai-attach"
                    className="text-sm font-semibold rounded-xl py-2.5 bg-white/5 border border-white/10 text-gray-200 hover:bg-white/10 flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors"><Paperclip className="w-3.5 h-3.5" /> {uploading ? t("Lädt…") : t("Datei anhängen")}</button>
                </div>
              </div>
            )}

            <div className="px-4 py-3 border-t border-white/10 shrink-0" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
              <div className="flex items-end gap-2">
                {!draft && (
                  <button onClick={() => chatFileRef.current?.click()} disabled={uploading || !conv} data-testid="ai-attach-inline" title={t("Datei anhängen")}
                    className="shrink-0 w-11 h-11 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 flex items-center justify-center disabled:opacity-40"><Paperclip className="w-4 h-4" /></button>
                )}
                <textarea id="ai-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1} disabled={!conv}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder={t("Ihre Nachricht…")} data-testid="ai-input"
                  className="flex-1 resize-none bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-[color:var(--client-accent)]/50 max-h-32" />
                <button onClick={sendMessage} disabled={!input.trim() || thinking || !conv} data-testid="ai-send-message"
                  className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-black disabled:opacity-40 active:scale-95 transition-transform" style={{ background: accent }}><Send className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-between mt-2.5">
                <button onClick={() => submitChat(true)} disabled={submitting || !conv} data-testid="ai-human"
                  className="inline-flex items-center gap-1.5 text-xs font-bold hover:opacity-80 transition-opacity disabled:opacity-40" style={{ color: accent }}>
                  <UserRound className="w-3.5 h-3.5" /> {t("Mit Mensch sprechen")}
                </button>
                <button onClick={goSupportCompose} data-testid="ai-manual"
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors inline-flex items-center gap-1">{t("Ohne KI direkt schreiben")} <ExternalLink className="w-3 h-3" /></button>
              </div>
            </div>
          </>
        ))}

        <input ref={chatFileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, "chat"); e.target.value = ""; }} accept="image/*,.pdf,.doc,.docx,.zip" data-testid="ai-file-input" />
        <input ref={wishFileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, "wish"); e.target.value = ""; }} accept="image/*,.pdf,.doc,.docx,.zip" data-testid="ai-wish-file-input" />
        <input ref={wishPhotoRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, "wish"); e.target.value = ""; }} accept="image/*" data-testid="ai-wish-photo-input" />
      </SheetContent>
    </Sheet>
  );
}

const QuickAction = ({ icon: Icon, title, desc, onClick, accent, testid, primary }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full text-left rounded-2xl p-4 flex items-start gap-3.5 border transition-all active:scale-[0.99] hover:-translate-y-0.5 ${primary ? "" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"}`}
    style={primary ? { background: `${accent}12`, borderColor: `${accent}55` } : {}}>
    <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1F`, color: accent }}><Icon className="w-5 h-5" /></span>
    <div className="min-w-0">
      <div className="font-bold text-sm">{title}</div>
      <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
    </div>
  </button>
);

const Bubble = ({ m, accent }) => {
  const isCust = m.sender_type === "customer";
  return (
    <div className={`flex gap-2.5 ${isCust ? "flex-row-reverse" : ""}`}>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
        style={isCust ? { background: "rgba(255,255,255,0.06)", color: "#9ca3af" } : { background: `${accent}1F`, color: accent }}>
        {isCust ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
      </span>
      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${isCust ? "bg-white/[0.06] text-gray-100" : "text-gray-200"}`}
        style={isCust ? {} : { background: `${accent}12`, border: `1px solid ${accent}22` }}>
        {m.message}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center gap-2 text-sm py-0.5">
    <span className="text-gray-500 w-20 shrink-0">{label}</span>
    <span className="font-semibold text-gray-200">{value}</span>
  </div>
);

const ConfirmScreen = ({ done, accent, t, onClose, goSupport }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="ai-confirm">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${accent}1A`, color: accent }}>
      {done.human ? <UserRound className="w-8 h-8" /> : <CheckCircle2 className="w-8 h-8" />}
    </div>
    <div className="text-lg font-black">{done.human ? t("Danke. Unser KEWAG-Team übernimmt Ihre Anfrage persönlich.") : t("Ihre Anfrage wurde an KEWAG gesendet.")}</div>
    <p className="text-sm text-gray-400 mt-2">
      {t("Sie finden den Supportfall jederzeit unter «Support» in Ihrem Cockpit.")}
      {done.ticket_no && <><br /><span className="text-gray-300 font-semibold">{t("Fall-Nummer")}: {done.ticket_no}</span></>}
    </p>
    <div className="flex flex-col gap-2 w-full mt-6 max-w-xs">
      <button onClick={goSupport} data-testid="ai-goto-support" className="text-black font-bold rounded-xl py-3 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform" style={{ background: accent }}>{t("Zum Supportfall")} <ArrowRight className="w-4 h-4" /></button>
      <button onClick={onClose} data-testid="ai-confirm-close" className="text-sm font-semibold text-gray-400 hover:text-white py-2 transition-colors">{t("Schliessen")}</button>
    </div>
  </div>
);
